import React, { useEffect, useState } from "react";
import {
  Card,
  Tabs,
  Table,
  Button,
  Modal,
  Input,
  Select,
  Switch,
  Space,
  message,
  Tag,
  Tooltip,
  Drawer,
  Typography,
  Grid,
  Popconfirm,
  Menu,
} from "antd";
import {
  ApiOutlined, ArrowLeftOutlined, QuestionCircleOutlined, EditOutlined, DeleteOutlined,
  ContactsOutlined, DollarOutlined, CheckSquareOutlined, TeamOutlined,
} from "@ant-design/icons";
import axios from "axios";
import {
  createFields,
  updateFieldApi,
  deleteFieldApi,
  fetchCrmFieldsApi,

  fetchLeadStatusApi,
  addLeadStatusApi,
  updateLeadStatusApi,
  deleteLeadStatusApi,

  fetchLeadSourceApi,
  addLeadSourceApi,
  updateLeadSourceApi,
  deleteLeadSourceApi,

  fetchLeadPurposeApi,
  addLeadPurposeApi,
  updateLeadPurposeApi,
  deleteLeadPurposeApi,
  fetchDealTypeApi,
  addDealTypeApi,
  updateDealTypeApi,
  deleteDealTypeApi,
  fetchTaskTypeApi,
  addTaskTypeApi,
  updateTaskTypeApi,
  deleteTaskTypeApi,

  fetchLeadImportHistoryApi,
  fetchLeadExportHistoryApi,

} from "../../../apiservice/crm";
import MasterTable from "./components/MasterTable";
import LeadImportHistory from "./components/LeadImportHistory";
import LeadExportHistory from "./components/LeadExportHistory";
import AuditLog from "./components/AuditLog";
import TeamsManager from "./components/TeamsManager";
import { useLocation, useNavigate } from "react-router-dom";
import MetaIntegration from "./components/MetaIntegration";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers } from "../../../store/commonThunks/lookups";
import ApiIntegration from "./components/ApiIntegration/ApiIntegration";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { isAdmin } from "../../../lib/functions";
import "../crmSharedStyle.css";

const { TabPane } = Tabs;
const { Title, Text } = Typography;

const FIELD_TYPES = [
  { label: "Text", value: "text" },
  { label: "Number", value: "number" },
  { label: "Dropdown", value: "dropdown" },
  { label: "Date", value: "date" },
  { label: "Multi Select", value: "multi_select" },
];

const DEFAULT_FIELD_FORM = {
  field_name: "",
  field_key: "",
  type: "text",
  show_in_list: true,
  show_in_filter: false,
  is_required: false,
  options: [],
};

const CRMCustomize = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const [activeTab, setActiveTab] = useState("lead");
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusList, setStatusList] = useState([]);
  const [sourceList, setSourceList] = useState([]);
  const [purposeList, setPurposeList] = useState([]);
  const [dealTypes, setDealTypes] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [importRuns, setImportRuns] = useState([]);
  const [importHistoryLoading, setImportHistoryLoading] = useState(false);
  const [exportLogs, setExportLogs] = useState([]);
  const [exportHistoryLoading, setExportHistoryLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // The field being edited, or null in "Add Field" mode. Type and
  // field_key are immutable once created (see updateCRMField on the
  // backend), so editing only ever touches name/flags/options.
  const [editingField, setEditingField] = useState(null);

  const [form, setForm] = useState(DEFAULT_FIELD_FORM);

  const [optionInput, setOptionInput] = useState("");

  const location = useLocation();

  const users = useSelector((s) => s.lookups.users);

  const authInfo = useSelector(selectAuthInfo);
  const isCrmBasicFreeVersion =
    String(authInfo?.subscription || "").toLowerCase() === "forthcrm_basic" &&
    String(authInfo?.trial_mode || "").toLowerCase() === "yes";
  const isAdminUser = isAdmin(authInfo);

  const dispatch = useDispatch();

  // 🔥 Load fields
  const loadFields = async (module) => {
    try {
      setLoading(true);
      const res = await fetchCrmFieldsApi({ module });
      setFields(res.data || []);
    } catch (err) {
      console.log(err);
      message.error("Failed to load fields");
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const res = await fetchLeadStatusApi();
      setStatusList(res.data || []);
    } catch {
      message.error("Failed to load status");
    }
  };

  const loadSource = async () => {
    try {
      const res = await fetchLeadSourceApi();
      setSourceList(res.data || []);
    } catch {
      message.error("Failed to load source");
    }
  };

  const loadPurpose = async () => {
    try {
      const res = await fetchLeadPurposeApi();
      setPurposeList(res.data || []);
    } catch {
      message.error("Failed to load purpose");
    }
  };

  const loadDealTypes = async () => {
    try {
      const res = await fetchDealTypeApi();
      setDealTypes(res.data || []);
    } catch {
      message.error("Failed to load deal types");
    }
  };

  const loadTaskTypes = async () => {
    try {
      const res = await fetchTaskTypeApi();
      setTaskTypes(res.data || []);
    } catch {
      message.error("Failed to load task types");
    }
  };

  const loadImportHistory = async () => {
    try {
      setImportHistoryLoading(true);
      const res = await fetchLeadImportHistoryApi();
      setImportRuns(res.data?.runs || []);
    } catch {
      message.error("Failed to load import history");
    } finally {
      setImportHistoryLoading(false);
    }
  };

  const loadExportHistory = async () => {
    try {
      setExportHistoryLoading(true);
      const res = await fetchLeadExportHistoryApi();
      setExportLogs(res.data?.logs || []);
    } catch {
      message.error("Failed to load export history");
    } finally {
      setExportHistoryLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const metaStatus = params.get("meta_status");
    const msg = params.get("message");
    const warning = params.get("warning");

    if (tab) setActiveTab(tab);

    if (metaStatus === "success") {
      message.success("Meta connected successfully");
    }

    if (metaStatus === "error") {
      message.error(msg || "Meta connection failed");
    }

    // A toast is easy to miss; this needs acknowledging since it means
    // leads from this page may silently go to a different branch.
    if (warning) {
      Modal.warning({
        title: "Heads up",
        content: warning,
        okText: "Got it",
      });
    }

    if (metaStatus) {
      navigate(
        {
          pathname: location.pathname,
          search: tab ? `?tab=${tab}` : "",
        },
        { replace: true }
      );
    }

  }, [location.search]);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const RESTRICTED_TABS = ["api-integration", "meta-integration"];

  useEffect(() => {
    if (isCrmBasicFreeVersion && RESTRICTED_TABS.includes(activeTab)) {
      setActiveTab("lead");
    }
  }, [isCrmBasicFreeVersion, activeTab]);

  // Export history, the audit log, and Teams are admin-only, matching their
  // actions' own server-side gating -- this just avoids showing a 403'd
  // empty tab.
  const ADMIN_ONLY_TABS = ["lead-export-history", "audit-log", "teams"];
  useEffect(() => {
    if (!isAdminUser && ADMIN_ONLY_TABS.includes(activeTab)) {
      setActiveTab("lead");
    }
  }, [isAdminUser, activeTab]);


  useEffect(() => {
    if (activeTab === "lead") {
      loadFields("lead");
    }

    if (activeTab === "deal") {
      loadFields("deal");
    }

    if (activeTab === "lead-status") {
      loadStatus();
    }

    if (activeTab === "lead-source") {
      loadSource();
    }

    if (activeTab === "lead-purpose") {
      loadPurpose();
    }
    if (activeTab === "deal-type") {
      loadDealTypes();
    }
    if (activeTab === "task-type") {
      loadTaskTypes();
    }
    if (activeTab === "lead-import-history") {
      loadImportHistory();
    }
    if (activeTab === "lead-export-history") {
      loadExportHistory();
    }
  }, [activeTab]);

  // Custom Fields Handlers and columns START

  // 🔥 Add option
  const addOption = () => {
    if (!optionInput) return;

    setForm((s) => ({
      ...s,
      options: [...s.options, optionInput],
    }));
    setOptionInput("");
  };

  // 🔥 Remove option
  const removeOption = (val) => {
    setForm((s) => ({
      ...s,
      options: s.options.filter((o) => o !== val),
    }));
  };

  const openAddField = () => {
    setEditingField(null);
    setForm(DEFAULT_FIELD_FORM);
    setModalOpen(true);
  };

  const openEditField = (row) => {
    setEditingField(row);
    setForm({
      field_name: row.field_name,
      field_key: row.field_key,
      type: row.type,
      show_in_list: !!row.show_in_list,
      show_in_filter: !!row.show_in_filter,
      is_required: !!row.is_required,
      options: row.options || [],
    });
    setModalOpen(true);
  };

  const handleDeleteField = async (id) => {
    try {
      await deleteFieldApi({ id });
      message.success("Field deleted");
      loadFields(activeTab);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Failed to delete field");
    }
  };

  // 🔥 Submit field (create or edit)
  const handleSubmit = async () => {
    if (!form.field_name) {
      return message.warning("Field name required");
    }

    try {
      if (editingField) {
        await updateFieldApi({ ...form, id: editingField.id });
        message.success("Field updated ✅");
      } else {
        await createFields({
          ...form,
          module: activeTab,
        });
        message.success("Field created ✅");
      }

      setModalOpen(false);
      setEditingField(null);
      setForm(DEFAULT_FIELD_FORM);

      loadFields(activeTab);
    } catch (err) {
      console.log(err);
      message.error(err?.response?.data?.message || "Failed ❌");
    }
  };

  // 🔥 Auto generate key
  const handleNameChange = (value) => {
    // field_key is immutable once a field exists (see updateCRMField) --
    // only regenerate it while creating a brand new field.
    if (editingField) {
      setForm((s) => ({ ...s, field_name: value }));
      return;
    }

    const key = value
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

    setForm((s) => ({
      ...s,
      field_name: value,
      field_key: key,
    }));
  };

  const columns = [
    {
      title: "Field",
      dataIndex: "field_name",
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: "List",
      dataIndex: "show_in_list",
      render: (v) => (v ? "Yes" : "No"),
    },
    {
      title: "Filter",
      dataIndex: "show_in_filter",
      render: (v) => (v ? "Yes" : "No"),
    },
    {
      title: "Required",
      dataIndex: "is_required",
      render: (v) => (v ? "Yes" : "No"),
    },
    {
      title: "Actions",
      render: (_, row) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEditField(row)} />
          <Popconfirm
            title="Delete this field?"
            description="Only possible while no lead/deal has a value saved for it."
            onConfirm={() => handleDeleteField(row.id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Custom Fields Handlers and columns END

  // Handlers for Status, Source, Purpose
  const handleCreateStatus = async (values) => {
    await addLeadStatusApi(values);
    loadStatus();
  };

  const handleUpdateStatus = async (values) => {
    await updateLeadStatusApi(values);
    loadStatus();
  };

  const handleDeleteStatus = async (id) => {
    try {
      await deleteLeadStatusApi({ id });

      message.success("Status deleted successfully");
      loadStatus();

    } catch (err) {
      message.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete status"
      );
    }
  };

  const handleCreateSource = async (values) => {
    await addLeadSourceApi(values);
    loadSource();
  };

  const handleUpdateSource = async (values) => {
    await updateLeadSourceApi(values);
    loadSource();
  };

  const handleDeleteSource = async (id) => {
    try {
      await deleteLeadSourceApi({ id });
      message.success("Source deleted successfully");
      loadSource();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
        "Failed to delete source"
      );
    }
  };

  const handleCreatePurpose = async (values) => {
    await addLeadPurposeApi(values);
    loadPurpose();
  };

  const handleUpdatePurpose = async (values) => {
    await updateLeadPurposeApi(values);
    loadPurpose();
  };

  const handleDeletePurpose = async (id) => {
    try {
      await deleteLeadPurposeApi({ id });
      message.success("Purpose deleted successfully");
      loadPurpose();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
        "Failed to delete purpose"
      );
    }
  };
  const handleCreateDealType = async (values) => {
    await addDealTypeApi(values);
    loadDealTypes();
  };

  const handleUpdateDealType = async (values) => {
    await updateDealTypeApi(values);
    loadDealTypes();
  };

  const handleDeleteDealType = async (id) => {
    try {
      await deleteDealTypeApi({ id });
      message.success("Deal type deleted successfully");
      loadDealTypes();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
        "Failed to delete deal type"
      );
    }
  };

  const handleCreateTaskType = async (values) => {
    await addTaskTypeApi(values);
    loadTaskTypes();
  };

  const handleUpdateTaskType = async (values) => {
    await updateTaskTypeApi(values);
    loadTaskTypes();
  };

  const handleDeleteTaskType = async (id) => {
    try {
      await deleteTaskTypeApi({ id });
      message.success("Task type deleted successfully");
      loadTaskTypes();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
        "Failed to delete task type"
      );
    }
  };

  // Grouped navigation, replacing the old flat horizontal tab bar (12 tabs
  // and growing no longer fit on one line -- see renderTabBar={() => null}
  // below, which hides Tabs' own bar in favor of this). Built from one
  // source of truth so the sidebar (desktop) and the dropdown (mobile) and
  // the existing admin/restricted-tab gating stay in sync automatically.
  const navGroups = [
    {
      key: "leads",
      label: "Leads",
      icon: <ContactsOutlined />,
      items: [
        { key: "lead", label: "Lead Custom Fields" },
        { key: "lead-status", label: "Lead Status" },
        { key: "lead-source", label: "Lead Source" },
        { key: "lead-purpose", label: "Lead Purpose" },
        { key: "lead-import-history", label: "Lead Import History" },
        isAdminUser && { key: "lead-export-history", label: "Lead Export History" },
      ].filter(Boolean),
    },
    {
      key: "deals",
      label: "Deals",
      icon: <DollarOutlined />,
      items: [
        { key: "deal", label: "Deal Custom Fields" },
        { key: "deal-type", label: "Deal Types" },
      ],
    },
    {
      key: "tasks",
      label: "Tasks",
      icon: <CheckSquareOutlined />,
      items: [{ key: "task-type", label: "Task Types" }],
    },
    isAdminUser && {
      key: "teams-access",
      label: "Teams & Access",
      icon: <TeamOutlined />,
      items: [
        { key: "teams", label: "Teams" },
        { key: "audit-log", label: "Audit Log" },
      ],
    },
    !isCrmBasicFreeVersion && {
      key: "integrations",
      label: "Integrations",
      icon: <ApiOutlined />,
      items: [
        { key: "api-integration", label: "Website / Public API" },
        { key: "meta-integration", label: "Meta Lead Ads" },
      ],
    },
  ].filter(Boolean);

  const menuItems = navGroups.map((g) => ({
    key: g.key,
    label: (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {g.icon} {g.label}
      </span>
    ),
    type: "group",
    children: g.items.map((i) => ({ key: i.key, label: i.label })),
  }));

  const flatTabOptions = navGroups.flatMap((g) =>
    g.items.map((i) => ({ value: i.key, label: `${g.label} — ${i.label}` }))
  );

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              style={{ marginRight: 8 }}
            />
            <Title level={4} style={{ margin: 0 }}>
              CRM Customization
            </Title>
          </div>

          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 20 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </div>
      }
    >
      <div style={{ display: "flex", gap: 24, flexDirection: screens.md ? "row" : "column" }}>
        {screens.md ? (
          <Menu
            mode="inline"
            selectedKeys={[activeTab]}
            defaultOpenKeys={navGroups.map((g) => g.key)}
            onClick={({ key }) => setActiveTab(key)}
            items={menuItems}
            style={{ width: 240, flexShrink: 0, border: "none" }}
          />
        ) : (
          <Select
            value={activeTab}
            onChange={setActiveTab}
            options={flatTabOptions}
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
          />
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
      <Tabs activeKey={activeTab} onChange={setActiveTab} renderTabBar={() => null}>

        {/* 🔥 LEADS */}
        <TabPane tab="Lead Custom Fields" key="lead">
          <Button
            type="primary"
            style={{ marginBottom: 12 }}
            onClick={openAddField}
          >
            + Add Field
          </Button>

          <Table
            rowKey="id"
            dataSource={fields}
            columns={columns}
            loading={loading}
            scroll={{ x: "max-content" }}
          />
        </TabPane>

        {/* 🔥 DEALS */}
        <TabPane tab="Deal Custom Fields" key="deal">
          <Button
            type="primary"
            style={{ marginBottom: 12 }}
            onClick={openAddField}
          >
            + Add Field
          </Button>

          <Table
            rowKey="id"
            dataSource={fields}
            columns={columns}
            loading={loading}
            scroll={{ x: "max-content" }}
          />
        </TabPane>

        {/* Status, Source, Purpose Tabs */}
        <TabPane tab="Lead Status" key="lead-status">
          <MasterTable
            title="Lead Status"
            type="Status"
            rows={statusList}
            loading={loading}
            onCreate={handleCreateStatus}
            onUpdate={handleUpdateStatus}
            onDelete={handleDeleteStatus}
          />
        </TabPane>

        <TabPane tab="Lead Source" key="lead-source">
          <MasterTable
            title="Lead Source"
            type="Source"
            rows={sourceList}
            loading={loading}
            onCreate={handleCreateSource}
            onUpdate={handleUpdateSource}
            onDelete={handleDeleteSource}
          />
        </TabPane>

        <TabPane tab="Lead Purpose" key="lead-purpose">
          <MasterTable
            title="Lead Purpose"
            type="Purpose"
            rows={purposeList}
            loading={loading}
            onCreate={handleCreatePurpose}
            onUpdate={handleUpdatePurpose}
            onDelete={handleDeletePurpose}
          />
        </TabPane>

        <TabPane tab="Lead Import History" key="lead-import-history">
          <LeadImportHistory runs={importRuns} loading={importHistoryLoading} />
        </TabPane>

        {isAdminUser && (
          <TabPane tab="Lead Export History" key="lead-export-history">
            <LeadExportHistory logs={exportLogs} loading={exportHistoryLoading} />
          </TabPane>
        )}

        {isAdminUser && (
          <TabPane tab="Audit Log" key="audit-log">
            <AuditLog users={users} />
          </TabPane>
        )}

        {isAdminUser && (
          <TabPane tab="Teams" key="teams">
            <TeamsManager users={users} />
          </TabPane>
        )}

        <TabPane tab="Deal Types" key="deal-type">
          <MasterTable
            title="Deal Types"
            type="Deal Type"
            rows={dealTypes}
            loading={loading}
            onCreate={handleCreateDealType}
            onUpdate={handleUpdateDealType}
            onDelete={handleDeleteDealType}
          />
        </TabPane>

        <TabPane tab="Task Types" key="task-type">
          <MasterTable
            title="Task Types"
            type="Task Type"
            rows={taskTypes}
            loading={loading}
            onCreate={handleCreateTaskType}
            onUpdate={handleUpdateTaskType}
            onDelete={handleDeleteTaskType}
            lockSystemRows
          />
        </TabPane>
        {!isCrmBasicFreeVersion && (
          <TabPane
            tab={
              <span>
                <ApiOutlined />
                Website / Public API
              </span>
            }
            key="api-integration"
          >
            <ApiIntegration users={users} />
          </TabPane>
        )}
        {!isCrmBasicFreeVersion && (
          <TabPane tab="Meta Lead Ads" key="meta-integration">
            <MetaIntegration refreshToken={location.search} users={users} />
          </TabPane>
        )}

      </Tabs>
        </div>
      </div>

      {/* 🔥 MODAL */}
      <Modal
        title={editingField ? "Edit Custom Field" : "Create Custom Field"}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditingField(null);
          setForm(DEFAULT_FIELD_FORM);
        }}
        onOk={handleSubmit}
        okText={editingField ? "Save" : "Create"}
        className="crm-modal-responsive"
      >
        <Input
          placeholder="Field Name"
          value={form.field_name}
          onChange={(e) => handleNameChange(e.target.value)}
          style={{ marginBottom: 10 }}
        />

        <Input
          placeholder="Field Key"
          value={form.field_key}
          disabled
          style={{ marginBottom: 10, display: "none" }}
        />

        <Select
          value={form.type}
          onChange={(v) => setForm((s) => ({ ...s, type: v }))}
          options={FIELD_TYPES}
          disabled={!!editingField}
          style={{ width: "100%", marginBottom: 10 }}
        />
        {editingField && (
          <Text type="secondary" style={{ display: "block", marginTop: -6, marginBottom: 10, fontSize: 12 }}>
            Type can't be changed after a field is created.
          </Text>
        )}

        {/* 🔥 OPTIONS */}
        {["dropdown", "multi_select"].includes(form.type) && (
          <>
            <Space style={{ marginBottom: 10 }}>
              <Input
                placeholder="Add option"
                value={optionInput}
                onChange={(e) => setOptionInput(e.target.value)}
              />
              <Button onClick={addOption}>Add</Button>
            </Space>

            <div>
              {form.options.map((o) => (
                <Tag
                  key={o}
                  closable
                  onClose={() => removeOption(o)}
                >
                  {o}
                </Tag>
              ))}
            </div>
          </>
        )}

        <div style={{ marginTop: 10 }}>
          <Space direction="vertical">

            <div>
              Show in List{" "}
              <Switch
                checked={form.show_in_list}
                onChange={(v) =>
                  setForm((s) => ({ ...s, show_in_list: v }))
                }
              />
            </div>

            <div>
              Show in Filter{" "}
              <Switch
                checked={form.show_in_filter}
                onChange={(v) =>
                  setForm((s) => ({ ...s, show_in_filter: v }))
                }
              />
            </div>

            <div>
              Required{" "}
              <Switch
                checked={form.is_required}
                onChange={(v) =>
                  setForm((s) => ({ ...s, is_required: v }))
                }
              />
            </div>

          </Space>
        </div>
      </Modal>
      <Drawer
        title="CRM Customization Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        className="crm-drawer"
        width={screens.md ? 430 : "100%"}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "12px"
            }}
          >
            CRM Customization
          </h2>

          <p style={{ color: "#555", marginBottom: "16px", marginLeft: 17 }}>
            This section helps you customize your CRM workflow based on your business needs.
            You can create additional fields, organize lead information, manage deal categories,
            and configure CRM options used throughout the system.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>What You Can Do</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>Create custom fields for Leads and Deals</li>
              <li>Add text, number, dropdown, multi-select, and date fields</li>
              <li>Create dropdown options for selection fields</li>
              <li>Control which fields appear in CRM lists</li>
              <li>Enable fields in search and filter sections</li>
              <li>Mark important fields as mandatory</li>
              <li>Manage lead statuses used in CRM workflow</li>
              <li>Manage lead sources like Website, Call, Reference, etc.</li>
              <li>Manage lead purposes and inquiry categories</li>
              <li>Create and manage deal types for sales tracking</li>
              <li>Customize CRM forms based on business requirements</li>
              <li>Connect Facebook / Instagram lead ads to import leads automatically into CRM</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Custom fields will appear automatically in CRM forms.</li>
              <li>Required fields must be filled before saving records.</li>
              <li>Dropdown fields can contain multiple selectable options.</li>
              <li>Fields enabled in filters help improve CRM searching.</li>
              <li>Changes made here affect CRM Leads and Deals modules.</li>
              <li>Keep field names simple and easy for users to understand.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </Card>
  );
};

export default CRMCustomize;